<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import { useAccount } from 'dashboard/composables/useAccount';
import { useVoiceCalls } from 'dashboard/composables/useVoiceCalls';
import { useDomainContext } from '../../../../../context/application-context';

import ChannelItem from 'dashboard/components/widgets/ChannelItem.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();
const { isVoiceCallsVisible } = useVoiceCalls();
const domainContext = useDomainContext();

const globalConfig = useMapGetter('globalConfig/get');

const enabledFeatures = computed(() => currentAccount.value?.features || {});

const hasTiktokConfigured = computed(() => {
  return window.chatwootConfig?.tiktokAppId;
});

const channelList = computed(() => {
  const { apiChannelName } = globalConfig.value;
  const channels = [
    {
      key: 'website',
      title: 'Website Chat',
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION'),
      icon: 'i-woot-website',
    },
    {
      key: 'facebook',
      title: 'Facebook Messenger',
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.DESCRIPTION'),
      icon: 'i-woot-messenger',
    },
    {
      key: 'whatsapp',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.DESCRIPTION'),
      icon: 'i-woot-whatsapp',
    },
    // {
    //   key: 'sms',
    //   title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.TITLE'),
    //   description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.DESCRIPTION'),
    //   icon: 'i-woot-sms',
    // },
    {
      key: 'email',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.DESCRIPTION'),
      icon: 'i-woot-mail',
    },
    {
      key: 'api',
      title: apiChannelName || t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION'),
      icon: 'i-woot-api',
    },
    {
      key: 'telegram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION'),
      icon: 'i-woot-telegram',
    },
    {
      key: 'max',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MAX.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MAX.DESCRIPTION'),
      icon: 'i-ri-message-3-fill',
    },
    ...(enabledFeatures.value.channel_green_api
      ? [
          {
            key: 'green_api_max',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.GREEN_API_MAX.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.GREEN_API_MAX.DESCRIPTION'
            ),
            icon: 'i-ri-message-3-fill',
          },
        ]
      : []),
    ...(enabledFeatures.value.channel_green_api_whatsapp
      ? [
          {
            key: 'green_api_whatsapp',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.GREEN_API_WHATSAPP.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.GREEN_API_WHATSAPP.DESCRIPTION'
            ),
            icon: 'i-woot-whatsapp',
          },
        ]
      : []),
    ...(enabledFeatures.value.channel_green_api_telegram
      ? [
          {
            key: 'green_api_telegram',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.GREEN_API_TELEGRAM.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.GREEN_API_TELEGRAM.DESCRIPTION'
            ),
            icon: 'i-woot-telegram',
          },
        ]
      : []),
    ...(enabledFeatures.value.channel_telegram_personal
      ? [
          {
            key: 'telegram_personal',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM_PERSONAL.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM_PERSONAL.DESCRIPTION'
            ),
            icon: 'i-woot-telegram',
          },
        ]
      : []),
    ...(enabledFeatures.value.channel_telegram_personal
      ? [
          {
            key: 'max_personal',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MAX_PERSONAL.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.MAX_PERSONAL.DESCRIPTION'
            ),
            icon: 'i-ri-message-3-fill',
          },
        ]
      : []),
    ...(enabledFeatures.value.channel_marketplace
      ? [
          {
            key: 'yandex_market',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.YANDEX_MARKET.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.YANDEX_MARKET.DESCRIPTION'
            ),
            icon: 'i-ri-store-3-fill',
          },
          {
            key: 'ozon',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.OZON.TITLE'),
            description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.OZON.DESCRIPTION'),
            icon: 'i-ri-shopping-bag-4-fill',
          },
          {
            key: 'wildberries',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WILDBERRIES.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.WILDBERRIES.DESCRIPTION'
            ),
            icon: 'i-ri-shopping-cart-2-fill',
          },
          {
            key: 'rocketdata',
            title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.ROCKETDATA.TITLE'),
            description: t(
              'INBOX_MGMT.ADD.AUTH.CHANNEL.ROCKETDATA.DESCRIPTION'
            ),
            icon: 'i-ri-map-pin-2-fill',
          },
        ]
      : []),
    // {
    //  key: 'line',
    //  title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.TITLE'),
    //  description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.DESCRIPTION'),
    //  icon: 'i-woot-line',
    // },
    {
      key: 'instagram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.DESCRIPTION'),
      icon: 'i-woot-instagram',
    },
    // {
    //   key: 'voice',
    //   title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.TITLE'),
    //   description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.DESCRIPTION'),
    //   icon: 'i-ri-phone-fill',
    // },
    ...(domainContext.vkAllowed
      ? [
          {
            key: 'vk',
            title: 'VK',
            description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VK.DESCRIPTION'),
            icon: 'i-ri-vk-fill',
          },
        ]
      : []),
  ];

  if (hasTiktokConfigured.value) {
    channels.push({
      key: 'tiktok',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TIKTOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TIKTOK.DESCRIPTION'),
      icon: 'i-woot-tiktok',
    });
  }

  // Upstream pushes both cards unconditionally and only greys them out; we hide
  // them until channel_voice is on, since calls are not launched yet.
  if (isVoiceCallsVisible.value) {
    channels.push({
      key: 'voice',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.DESCRIPTION'),
      icon: 'i-woot-voice',
    });

    channels.push({
      key: 'whatsapp_call',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP_CALL.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP_CALL.DESCRIPTION'),
      icon: 'i-woot-whatsapp',
    });
  }

  return channels;
});

const initChannelAuth = channel => {
  const params = {
    sub_page: channel,
    accountId: accountId.value,
  };
  router.push({ name: 'settings_inboxes_page_channel', params });
};
</script>

<template>
  <div
    class="grid max-w-3xl grid-cols-1 xs:grid-cols-2 mx-0 gap-6 sm:grid-cols-3 p-8"
  >
    <ChannelItem
      v-for="channel in channelList"
      :key="channel.key"
      :channel="channel"
      :enabled-features="enabledFeatures"
      @channel-item-click="initChannelAuth"
    />
  </div>
</template>
